'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Building2, 
  Users, 
 
  TrendingUp,
  Calendar,
  ArrowRight,
  Award,
  Target,
  Lightbulb
} from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'

import { FadeUp } from '@/components/ui/fade-up'
const caseStudies = [
  {
    id: 'hiroshima-city',
    category: '行政DX',
    client: '広島市',
    title: '窓口業務のデジタル化で市民サービスを大幅改善',
    description: '住民票発行や各種申請のオンライン化により、窓口での待ち時間を70%削減。年間10万件以上の申請がオンラインに移行。',
    image: '/images/case-studies/hiroshima-city.jpg',
    results: [
      { label: '待ち時間削減', value: '70%', icon: TrendingUp },
      { label: 'オンライン申請率', value: '85%', icon: Users },
      { label: '満足度向上', value: '92%', icon: Award }
    ],
    technologies: ['自治体向けCRM', 'オンライン申請システム', 'AI問い合わせ対応'],
    period: '2023年4月〜2024年3月',
    featured: true
  },
  {
    id: 'onomichi-tourism',
    category: '観光DX',
    client: '尾道市観光協会',
    title: 'デジタル観光パスで観光客の周遊を促進',
    description: 'QRコードを活用したデジタル観光パスを導入。観光スポットの混雑状況をリアルタイムで可視化し、観光客の分散に成功。',
    image: '/images/case-studies/onomichi-tourism.jpg',
    results: [
      { label: '観光客満足度', value: '+30%', icon: TrendingUp },
      { label: '平均滞在時間', value: '+2.5時間', icon: Calendar },
      { label: '観光消費額', value: '+45%', icon: Target }
    ],
    technologies: ['観光DXプラットフォーム', 'デジタル観光パス', 'AR観光ガイド'],
    period: '2023年7月〜継続中',
    featured: true
  },
  {
    id: 'kure-shopping',
    category: '商業振興',
    client: '呉市商店街連合会',
    title: 'デジタル地域通貨で地域経済を活性化',
    description: 'ブロックチェーン技術を活用したデジタル地域通貨を導入。地域内での消費循環を促進し、商店街の売上向上に貢献。',
    image: '/images/case-studies/kure-shopping.jpg',
    results: [
      { label: '加盟店舗数', value: '156店舗', icon: Building2 },
      { label: '月間流通額', value: '2,500万円', icon: TrendingUp },
      { label: '地域内消費率', value: '+15%', icon: Target }
    ],
    technologies: ['地域商店街DX', 'デジタル地域通貨', 'データ分析基盤'],
    period: '2023年10月〜継続中',
    featured: false
  },
  {
    id: 'sera-agriculture',
    category: '農業DX',
    client: '世羅町農業協同組合',
    title: 'スマート農業で収穫量と品質を向上',
    description: 'IoTセンサーとAI予測により、最適な栽培環境を実現。若手農家の技術習得も支援し、持続可能な農業を推進。',
    image: '/images/case-studies/sera-agriculture.jpg',
    results: [
      { label: '収穫量増加', value: '+20%', icon: TrendingUp },
      { label: '品質向上', value: 'A級品率85%', icon: Award },
      { label: '作業時間削減', value: '-30%', icon: Calendar }
    ],
    technologies: ['スマート農業システム', 'IoT環境モニタリング', 'AI収穫予測'],
    period: '2023年5月〜継続中',
    featured: false
  },
  {
    id: 'fukuyama-education',
    category: '教育',
    client: '福山市教育委員会',
    title: 'オンライン学習で教育格差を解消',
    description: '全市立学校にオンライン学習環境を整備。不登校児童への学習支援や、個別最適化された学習を実現。',
    image: '/images/case-studies/fukuyama-education.jpg',
    results: [
      { label: '学習効果', value: '+25%', icon: TrendingUp },
      { label: '不登校児童の学習参加', value: '78%', icon: Users },
      { label: '教員の負担軽減', value: '-40%', icon: Calendar }
    ],
    technologies: ['教育DXプラットフォーム', 'オンライン授業配信', 'AI個別指導'],
    period: '2024年1月〜継続中',
    featured: false
  },
  {
    id: 'mihara-community',
    category: '地域コミュニティ',
    client: '三原市',
    title: '地域ポータルアプリで住民参加が活性化',
    description: '地域情報の一元化と住民同士の交流機能により、地域活動への参加率が大幅に向上。防災情報の迅速な共有も実現。',
    image: '/images/case-studies/mihara-community.jpg',
    results: [
      { label: 'アプリ利用率', value: '65%', icon: Users },
      { label: 'イベント参加率', value: '2倍', icon: Calendar },
      { label: '防災情報到達率', value: '95%', icon: Target }
    ],
    technologies: ['地域ポータルアプリ', 'プッシュ通知システム', 'コミュニティ機能'],
    period: '2023年9月〜継続中',
    featured: false
  }
]

export function CaseStudiesContent() {
  const featuredCases = caseStudies.filter(c => c.featured)
  const otherCases = caseStudies.filter(c => !c.featured)

  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4">地域DXの成功事例</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              導入事例
            </h1>
            <p className="text-xl text-muted-foreground">
              様々な地域で実現したDXの成功事例をご紹介。<br />
              具体的な成果と導入プロセスをご覧ください。
            </p>
          </div>
        </div>
      </section>

      {/* 注目の事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">注目の導入事例</h2>
            <p className="text-lg text-muted-foreground">
              大きな成果を上げた代表的な事例をピックアップ
            </p>
          </FadeUp>

          <div className="grid gap-12">
            {featuredCases.map((caseStudy, index) => (
              <FadeUp delay={index * 0.1}
                key={caseStudy.id}
              >
                <Card className="overflow-hidden">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="aspect-[4/3] bg-gray-100 relative">
                      <Image
                        src={caseStudy.image}
                        alt={caseStudy.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center gap-2 mb-4">
                        <Badge variant="outline">{caseStudy.category}</Badge>
                        <span className="text-sm text-muted-foreground">{caseStudy.client}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-4">{caseStudy.title}</h3>
                      <p className="text-muted-foreground mb-6">{caseStudy.description}</p>
                      
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {caseStudy.results.map((result, idx) => {
                          const Icon = result.icon
                          return (
                            <div key={idx} className="text-center">
                              <Icon className="h-8 w-8 text-primary mx-auto mb-2" />
                              <div className="text-2xl font-bold">{result.value}</div>
                              <div className="text-xs text-muted-foreground">{result.label}</div>
                            </div>
                          )
                        })}
                      </div>

                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-semibold mb-2">導入技術</p>
                          <div className="flex flex-wrap gap-2">
                            {caseStudy.technologies.map((tech, idx) => (
                              <Badge key={idx} variant="secondary">{tech}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            <Calendar className="inline h-4 w-4 mr-1" />
                            {caseStudy.period}
                          </span>
                          <Button asChild>
                            <Link href={`/case-studies/${caseStudy.id}`}>
                              詳細を見る
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* その他の事例 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">その他の導入事例</h2>
            <p className="text-lg text-muted-foreground">
              様々な分野での成功事例をご紹介
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherCases.map((caseStudy, index) => (
              <FadeUp delay={index * 0.1}
                key={caseStudy.id}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-gray-100 relative">
                    <Image
                      src={caseStudy.image}
                      alt={caseStudy.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{caseStudy.category}</Badge>
                      <span className="text-sm text-muted-foreground">{caseStudy.client}</span>
                    </div>
                    <CardTitle className="line-clamp-2">{caseStudy.title}</CardTitle>
                    <CardDescription className="line-clamp-3">
                      {caseStudy.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      {caseStudy.results.slice(0, 2).map((result, idx) => {
                        const Icon = result.icon
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">{result.value}</span>
                          </div>
                        )
                      })}
                    </div>
                    <Button asChild className="w-full">
                      <Link href={`/case-studies/${caseStudy.id}`}>
                        詳細を見る
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <Lightbulb className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              あなたの地域でも、DXを始めませんか？
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              各地域の特性に合わせた最適なソリューションをご提案します。<br />
              まずは無料相談から始めましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg">
                無料相談を申し込む
              </Button>
              <Button size="lg" variant="outline">
                資料をダウンロード
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}